import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ScanLine, AlertTriangle, Check, RotateCcw, Keyboard, Camera, WifiOff } from "lucide-react";
import { enqueue, listAllCaches } from "@/lib/offlineDb";
import CameraScanner from "@/components/CameraScanner";

const REASONS = ["Incorrect Address", "Refused by Recipient", "Other"];

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("en-IN"); } catch { return s; }
}

function OfflineBanner() {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  if (online) return null;
  return (
    <div data-testid="rto-offline-banner" className="mt-3 flex items-center gap-2 rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      <WifiOff size={14} />
      <span>Offline mode · Looking up QR from local cache. New RTO scans will be queued and synced when you're back online.</span>
    </div>
  );
}

export default function DispatchRTO() {
  const [qr, setQr] = useState("");
  const [record, setRecord] = useState(null);
  const [searching, setSearching] = useState(false);
  const [reason, setReason] = useState("Incorrect Address");
  const [notes, setNotes] = useState("");
  const [redispatch, setRedispatch] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState([]);
  const [mode, setMode] = useState("manual"); // 'manual' | 'camera'

  const search = async (code = qr) => {
    if (!code) { toast.error("Enter or scan a QR / dispatch code"); return; }
    setSearching(true); setRecord(null);
    if (!navigator.onLine) {
      // Walk every cached label set looking for this QR string.
      try {
        const caches = await listAllCaches();
        for (const c of caches) {
          const hit = (c.items || []).find((x) => x.qr_code_string === code);
          if (hit) { setRecord(hit); setSearching(false); return; }
        }
        toast.error("Offline · QR not found in cached dispatches. Cache the issue while online first.");
      } catch (e) { toast.error("Cache lookup failed: " + e.message); }
      finally { setSearching(false); }
      return;
    }
    try {
      const { data } = await api.get(`/dispatch/by-qr/${encodeURIComponent(code)}`);
      setRecord(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSearching(false); }
  };

  const submit = async () => {
    if (!record) return;
    setSubmitting(true);
    const payload = {
      qr_code_string: record.qr_code_string,
      rto_reason: reason,
      notes: notes || null,
      redispatch,
    };
    // Offline path: stash in IndexedDB queue, sync worker will post when online.
    if (!navigator.onLine) {
      try {
        await enqueue("rto", payload, {
          subscriber_name: record.address_snapshot?.name,
          qr: record.qr_code_string,
        });
        toast.success(`Queued offline · ${record.qr_code_string} will sync when online`);
        setRecent((r) => [{ qr_code_string: record.qr_code_string, dispatch_status: redispatch ? "redispatched" : "rto", address_snapshot: record.address_snapshot, _at: new Date().toISOString(), _queued: true }, ...r].slice(0, 10));
        setRecord(null); setQr(""); setNotes(""); setReason("Incorrect Address"); setRedispatch(false);
      } catch (e) { toast.error("Queue failed: " + e.message); }
      finally { setSubmitting(false); }
      return;
    }
    try {
      const { data } = await api.post("/dispatch/rto", payload);
      toast.success(`Marked ${data.dispatch_status.toUpperCase()} · ${record.qr_code_string}`);
      setRecent((r) => [{ ...data, _at: new Date().toISOString() }, ...r].slice(0, 10));
      setRecord(null); setQr(""); setNotes(""); setReason("Incorrect Address"); setRedispatch(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="dispatch-rto" className="mx-auto max-w-3xl">
      <Button variant="ghost" size="sm" asChild className="mb-3 rounded-sm text-slate-600">
        <Link to="/dispatch"><ArrowLeft size={14} className="mr-1" />Back to Dispatch</Link>
      </Button>
      <h1 className="text-3xl font-semibold tracking-tight text-slate-900">RTO Scan</h1>
      <p className="mt-1 max-w-xl text-sm text-slate-500">Camera mode uses your device camera (mobile-friendly). Manual mode accepts USB/Bluetooth scanners or typed codes.</p>
      <OfflineBanner />

      <div className="mt-4 inline-flex rounded-sm border border-slate-200 bg-white p-1" role="tablist">
        <button data-testid="rto-mode-manual" onClick={() => setMode("manual")} className={`flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-sm font-medium ${mode === "manual" ? "bg-[#002FA7] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <Keyboard size={14} />Manual / Scanner
        </button>
        <button data-testid="rto-mode-camera" onClick={() => setMode("camera")} className={`flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-sm font-medium ${mode === "camera" ? "bg-[#002FA7] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
          <Camera size={14} />Camera
        </button>
      </div>

      {mode === "camera" && (
        <div className="mt-4">
          <CameraScanner onDetect={(text) => { setQr(text); search(text); }} />
        </div>
      )}

      <div className="mt-6 rounded-sm border border-slate-200 bg-white p-5">
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Dispatch QR / Code</Label>
        <div className="mt-1 flex gap-2">
          <div className="relative flex-1">
            <ScanLine size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              autoFocus
              value={qr}
              onChange={(e) => setQr(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
              data-testid="rto-qr-input"
              placeholder="DSP-MMYYYY-NNNNN"
              className="rounded-sm pl-9 font-mono"
            />
          </div>
          <Button onClick={() => search()} disabled={searching} data-testid="rto-search-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
            {searching ? "Searching…" : "Lookup"}
          </Button>
        </div>
      </div>

      {record && (
        <div className="mt-4 rounded-sm border border-rose-200 bg-rose-50/40 p-5" data-testid="rto-record">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-rose-100 text-rose-700"><AlertTriangle size={18} /></div>
            <div className="flex-1">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-rose-700">Dispatch Found</div>
              <div className="text-lg font-semibold text-slate-900">{record.address_snapshot?.name}</div>
              <div className="mt-0.5 font-mono text-xs text-slate-500">{record.qr_code_string} · {record.subscriber_code_snapshot} · Issue {record.issue_month_year}</div>
              <div className="mt-2 text-sm text-slate-700">
                {record.address_snapshot?.address_line_1}
                {record.address_snapshot?.address_line_2 ? ", " + record.address_snapshot.address_line_2 : ""}, {record.address_snapshot?.city}
                <span className="ml-2 font-mono">PIN {record.address_snapshot?.pincode}</span>
              </div>
              <div className="mt-1 text-xs text-slate-500">Postage: {record.postage_class} · Status: {record.dispatch_status}</div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="rto-reason-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm">
                <input type="checkbox" checked={redispatch} onChange={(e) => setRedispatch(e.target.checked)} data-testid="rto-redispatch" />
                Add to Re-dispatch Queue (after correction)
              </label>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Notes (optional)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-sm" data-testid="rto-notes" />
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRecord(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={submit} disabled={submitting} data-testid="rto-submit-btn" className="rounded-sm bg-rose-600 text-white hover:bg-rose-700">
              {redispatch ? <RotateCcw size={14} className="mr-1.5" /> : <Check size={14} className="mr-1.5" />}
              {submitting ? "Saving…" : redispatch ? "Mark RTO + Re-dispatch" : "Mark as RTO"}
            </Button>
          </div>
          {reason === "Incorrect Address" && (
            <div className="mt-3 rounded-sm bg-amber-50 px-3 py-2 text-xs text-amber-800">
              MOCKED: WhatsApp will be sent to subscriber {record.address_snapshot?.mobile_no} asking to update address.
            </div>
          )}
        </div>
      )}

      {recent.length > 0 && (
        <div className="mt-6 rounded-sm border border-slate-200 bg-white p-4" data-testid="rto-recent">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Recent in this session</div>
          <ul className="divide-y divide-slate-100 text-sm">
            {recent.map((r, i) => (
              <li key={i} className="flex items-center justify-between py-2">
                <div>
                  <div className="font-medium text-slate-900">{r.address_snapshot?.name}</div>
                  <div className="font-mono text-[11px] text-slate-500">{r.qr_code_string}</div>
                </div>
                <div className="text-right text-xs">
                  <div className="font-medium text-rose-700">{r.dispatch_status.toUpperCase()}</div>
                  <div className="text-slate-400">{fmtDate(r._at)}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
